import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, ListGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

export default function UserSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search users as they type
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const { users } = await api.searchUsers(searchQuery);
        setSearchResults(users);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelectUser = (username) => {
    setSearchQuery('');
    setShowResults(false);
    navigate(`/${username}`);
  };

  return (
    <div className="position-relative" ref={searchRef} style={{ width: '250px' }}>
      <InputGroup size="sm">
        <InputGroup.Text 
          style={{ 
            backgroundColor: theme === 'dark' ? '#2d3343' : '#e9ecef',
            borderColor: theme === 'dark' ? '#4f9eff' : '#ced4da',
            color: theme === 'dark' ? '#8b92a7' : '#495057'
          }}
        >
          <i className="bi bi-search"></i>
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
          style={{ 
            backgroundColor: theme === 'dark' ? '#2d3343' : '#fff',
            borderColor: theme === 'dark' ? '#4f9eff' : '#ced4da',
            color: theme === 'dark' ? '#fff' : '#212529'
          }}
        />
        {loading && (
          <InputGroup.Text 
            style={{ 
              backgroundColor: theme === 'dark' ? '#2d3343' : '#e9ecef',
              borderColor: theme === 'dark' ? '#4f9eff' : '#ced4da'
            }}
          >
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {showResults && searchResults.length > 0 && (
        <ListGroup 
          className="position-absolute w-100 mt-1 shadow-lg" 
          style={{ 
            zIndex: 1050,
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: theme === 'dark' ? '#1a1d29' : '#fff',
            border: `1px solid ${theme === 'dark' ? '#2d3343' : '#dee2e6'}`
          }}
        >
          {searchResults.map((user) => (
            <ListGroup.Item
              key={user._id}
              action
              onClick={() => handleSelectUser(user.username)}
              style={{
                backgroundColor: theme === 'dark' ? '#1a1d29' : '#fff',
                color: theme === 'dark' ? '#fff' : '#212529',
                borderColor: theme === 'dark' ? '#2d3343' : '#dee2e6'
              }}
              className="d-flex align-items-center"
            >
              {user.profilePicture ? (
                <img
                  src={api.getImageUrl(user.profilePicture)}
                  alt={user.username}
                  className="rounded-circle me-2"
                  width="32"
                  height="32"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <div 
                  className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                  style={{ 
                    width: '32px', 
                    height: '32px', 
                    backgroundColor: '#4f9eff',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="fw-bold">{user.displayName || user.username}</div>
                <small className="text-muted">@{user.username}</small>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
        <ListGroup 
          className="position-absolute w-100 mt-1 shadow" 
          style={{ 
            zIndex: 1050,
            backgroundColor: theme === 'dark' ? '#1a1d29' : '#fff'
          }}
        >
          <ListGroup.Item
            style={{
              backgroundColor: theme === 'dark' ? '#1a1d29' : '#fff',
              color: theme === 'dark' ? '#8b92a7' : '#6c757d',
              borderColor: theme === 'dark' ? '#2d3343' : '#dee2e6'
            }}
          >
            No users found
          </ListGroup.Item>
        </ListGroup>
      )}
    </div>
  );
}